/**
 * Named healthcare engineers — the single source for the "Healthcare
 * engineering" section on /team, the "Built by" row on case studies, and the
 * Person JSON-LD on /team. Facts (years, projects, who built what) are
 * owner-confirmed (4 Nov 2026); only list work a person actually did, and
 * never claim a standard (FHIR, HL7…) that none of their case studies show.
 */
import type { Locale } from "@/lib/i18n";

type Lang = "en" | "de" | "fr";
type Text = Record<Lang, string>;

export interface HealthcareEngineer {
  /** Anchor id on /team. */
  id: string;
  name: string;
  imageUrl: string;
  linkedin: string;
  github: string;
  yearsInHealthcare: number;
  role: Text;
  /** Case-study slug → what this person did there. */
  work: { slug: string; project: string; text: Text }[];
  stack: string[];
  /** schema.org knowsAbout. */
  knowsAbout: string[];
}

export const HEALTHCARE_TEAM: HealthcareEngineer[] = [
  {
    id: "muhammad-huzaifa",
    name: "Muhammad Huzaifa",
    imageUrl: "/users/huzaifa.png",
    linkedin: "https://www.linkedin.com/in/muhammad-huzaifa-ali-374612245/",
    github: "https://github.com/huzaifawoltrio",
    yearsInHealthcare: 3,
    role: {
      en: "Full Stack Developer, backend & clinical AI",
      de: "Full-Stack-Entwickler, Backend & klinische KI",
      fr: "Développeur full stack, backend et IA clinique",
    },
    work: [
      {
        slug: "linkwell-physician-referral-system",
        project: "LinkWell",
        text: {
          en: "athenahealth EHR integration that reads patient data and writes SOAP notes back to the chart.",
          de: "athenahealth-EHR-Integration, die Patientendaten liest und SOAP-Notizen in die Akte zurückschreibt.",
          fr: "Intégration du DSE athenahealth qui lit les données patient et réécrit les notes SOAP dans le dossier.",
        },
      },
      {
        slug: "daisyai---intelligent-emr",
        project: "Daisy.ai",
        text: {
          en: "Built the FastAPI backend and live audio pipeline for an ambient clinical assistant that drafts medical notes (Whisper and GPT-4o) for one-click physician sign-off.",
          de: "Entwickelte das FastAPI-Backend und die Live-Audio-Pipeline für einen klinischen Sprachassistenten, der medizinische Notizen (Whisper und GPT-4o) zur Freigabe per Klick durch den Arzt entwirft.",
          fr: "A développé le backend FastAPI et le pipeline audio en direct d'un assistant clinique vocal qui rédige les notes médicales (Whisper et GPT-4o) pour validation en un clic par le médecin.",
        },
      },
      {
        slug: "acolide-ai-ehr-clinic-management-saas",
        project: "Acolide",
        text: {
          en: "AI clinical scribe that turns visit conversations into SOAP notes (AWS HealthScribe), plus EHR backend services.",
          de: "KI-Dokumentationsassistent, der Gespräche in SOAP-Notizen umwandelt (AWS HealthScribe), sowie EHR-Backend-Dienste.",
          fr: "Scribe clinique IA qui transforme les consultations en notes SOAP (AWS HealthScribe), et services backend du dossier médical.",
        },
      },
      {
        slug: "grace-family---patient-care-portal",
        project: "Grace Family",
        text: {
          en: "The API behind the patient portal: insurance-card capture and the e-prescribing integration.",
          de: "Die API hinter dem Patientenportal: Erfassung der Versichertenkarte und die E-Rezept-Integration.",
          fr: "L'API du portail patient : capture de la carte d'assurance et intégration de la e-prescription.",
        },
      },
    ],
    stack: ["Python", "FastAPI", "PostgreSQL", "AWS"],
    knowsAbout: ["athenahealth integration", "EHR integration", "Clinical documentation AI", "SOAP notes", "EHR backend development", "e-prescribing integration", "HIPAA", "Python", "FastAPI", "AWS HealthScribe"],
  },
  {
    id: "muhammad-talha",
    name: "Muhammad Talha",
    imageUrl: "/users/talha.jpg",
    linkedin: "https://www.linkedin.com/in/muhammad-talha-7480492b0",
    github: "https://github.com/talhary",
    yearsInHealthcare: 2,
    role: {
      en: "Full Stack Developer, frontend & Node.js",
      de: "Full-Stack-Entwickler, Frontend & Node.js",
      fr: "Développeur full stack, frontend et Node.js",
    },
    work: [
      {
        slug: "acolide-ai-ehr-clinic-management-saas",
        project: "Acolide",
        text: {
          en: "Next.js and React front end for digital intake, e-signature consent and visual charting.",
          de: "Next.js- und React-Frontend für digitale Aufnahme, Einwilligung per E-Signatur und visuelle Dokumentation.",
          fr: "Frontend Next.js et React pour l'admission numérique, le consentement par signature électronique et le charting visuel.",
        },
      },
      {
        slug: "grace-family---patient-care-portal",
        project: "Grace Family",
        text: {
          en: "Patient portal, WebRTC video visits and Stripe co-pay billing.",
          de: "Patientenportal, WebRTC-Videosprechstunden und Zuzahlungsabrechnung über Stripe.",
          fr: "Portail patient, téléconsultations vidéo WebRTC et paiement du ticket modérateur via Stripe.",
        },
      },
      {
        slug: "nerihya-hyperlocal-marketplace-app-for-handyperson-services",
        project: "Nerihya",
        text: {
          en: "Mobile marketplace app and Node.js backend.",
          de: "Mobile Marktplatz-App und Node.js-Backend.",
          fr: "Application mobile de marketplace et backend Node.js.",
        },
      },
    ],
    stack: ["React", "Next.js", "Node.js", "WebRTC"],
    knowsAbout: ["Patient portals", "Telehealth", "EHR front-end development", "Digital patient intake", "HIPAA", "React", "Next.js", "Node.js"],
  },
];

export const HEALTHCARE_TEAM_COPY: Record<Lang, {
  title: string;
  subtitle: string;
  years: (n: number) => string;
  practices: string;
  builtBy: string;
}> = {
  en: {
    title: "Healthcare engineering",
    subtitle: "The engineers behind our healthcare case studies, and exactly what each of them built.",
    years: (n) => `${n} years building healthcare software`,
    practices: "Synthetic data only · annual HIPAA training",
    builtBy: "Built by",
  },
  de: {
    title: "Entwicklung für das Gesundheitswesen",
    subtitle: "Die Entwickler hinter unseren Fallstudien aus dem Gesundheitswesen, und was jeder von ihnen gebaut hat.",
    years: (n) => `${n} Jahre Softwareentwicklung im Gesundheitswesen`,
    practices: "Nur synthetische Daten · jährliche HIPAA-Schulung",
    builtBy: "Entwickelt von",
  },
  fr: {
    title: "Ingénierie santé",
    subtitle: "Les ingénieurs derrière nos études de cas santé, et ce que chacun a réellement construit.",
    years: (n) => `${n} ans de développement de logiciels de santé`,
    practices: "Données synthétiques uniquement · formation HIPAA annuelle",
    builtBy: "Réalisé par",
  },
};

export function healthcareLang(locale: Locale | string): Lang {
  const l = locale.slice(0, 2);
  return l === "de" || l === "fr" ? l : "en";
}

/** Engineers who worked on a case study, for its "Built by" row. */
export function buildersOf(slug: string): HealthcareEngineer[] {
  return HEALTHCARE_TEAM.filter((p) => p.work.some((w) => w.slug === slug));
}
